import { Controller } from '@hotwired/stimulus'

// TODO: Move it out of the Risk context. This class must be applicable to every linkage form

export default class extends Controller {
  static targets = ['checkbox', 'selectedCount', 'inactiveRow']

  declare checkboxTargets: HTMLInputElement[]
  declare readonly inactiveRowTargets: HTMLTableRowElement[]
  declare hasCheckboxTarget: boolean
  private originalHiddenInputValue: string = ''
  private form: HTMLFormElement | null = null
  private readonly handleDiscardedChanges = (): void => {
    setTimeout(() => {
      this.renderSelectedChips({ removable: false })
      this.syncCheckboxStates()
    }, 0)
  }

  declare checkboxState: Record<string, boolean>

  declare handleFrameRender: (event: Event) => void
  close: any

  connect (): void {
    this.originalHiddenInputValue = this.copyHiddenInputValue()
    this.syncCheckboxStates()
    this.form = this.element.closest('form')
    this.form?.addEventListener('cancel-changes:discarded', this.handleDiscardedChanges)

    this.handleFrameRender = this._handleFrameRender.bind(this)
    document.addEventListener('turbo:frame-render', this.handleFrameRender)
  }

  disconnect (): void {
    this.form?.removeEventListener('cancel-changes:discarded', this.handleDiscardedChanges)
    document.removeEventListener('turbo:frame-render', this.handleFrameRender)
  }

  copyHiddenInputValue (): string {
    const hiddenInput = document.querySelector<HTMLInputElement>('#selected-control-ids')
    return hiddenInput?.value ?? ''
  }

  checkPreviouslySelectedCheckbox (): void {
    const container = document.querySelector<HTMLDivElement>('#selected-control-hidden-inputs')
    if (container == null) return

    const hiddenInputs = container.querySelectorAll<HTMLInputElement>('input[type="hidden"][name="selected_control_ids[]"]')
    const selectedIds = Array.from(hiddenInputs).map(input => input.value)

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = selectedIds.includes(checkbox.value)
    })
  }

  checkPreviouslySelected (): void {
    const hiddenInput = document.querySelector<HTMLInputElement>('#selected-control-ids')
    if (hiddenInput == null) return

    const selectedIds = hiddenInput.value.split(',').map(id => id.trim()).filter(Boolean)

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = selectedIds.includes(checkbox.value)
    })
  }

  syncCheckboxStates (): void {
    const idsFromCommaInput = document.querySelector<HTMLInputElement>('#selected-control-ids')?.value
    const idsFromHiddenInputs = Array.from(
      document.querySelectorAll<HTMLInputElement>('input[type="hidden"][name="selected_control_ids[]"]')
    )
      .map(input => input.value.trim())
      .filter(id => id !== '')

    const commaInputIds = (idsFromCommaInput ?? '')
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '')

    const allIds = [...idsFromHiddenInputs, ...commaInputIds]
    const selectedIds = new Set(allIds)

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = selectedIds.has(checkbox.value)
    })

    // an unchecked inactive control cannot be linked again; Cancel discards the change
    this.inactiveRowTargets.forEach((row) => {
      const checkbox = row.querySelector<HTMLInputElement>('input[type="checkbox"]')
      if (checkbox != null) checkbox.disabled = !checkbox.checked
    })

    const hiddenAllControls = document.querySelector<HTMLInputElement>('#all-controls')
    let activeSelectedCount = 0

    if (hiddenAllControls != null) {
      try {
        const allControls: Array<[number, string, boolean]> = JSON.parse(hiddenAllControls.value)
        activeSelectedCount = allControls.filter(([id, _name, isActive]) =>
          selectedIds.has(String(id)) && isActive
        ).length
      } catch (e) {
        console.error('Invalid JSON in #all-controls', e)
      }
    }

    const countEl = document.querySelector('[data-controls-treatments-target="selectedCount"]')
    const name = activeSelectedCount === 1 ? 'internal control' : 'internal controls'
    if (countEl != null) {
      countEl.textContent = `${activeSelectedCount} ${name} selected`
    }
  }

  cancelSelection (event: any): void {
    const hiddenInput = document.querySelector<HTMLInputElement>('#selected-control-ids')
    const container = document.querySelector<HTMLDivElement>('#selected-control-hidden-inputs')

    if ((hiddenInput != null) && (container != null)) {
      hiddenInput.value = this.originalHiddenInputValue
      this.notifyHiddenInputChanged(hiddenInput)

      container.innerHTML = ''

      const ids = this.originalHiddenInputValue.split(',').filter(Boolean)
      ids.forEach(id => {
        const hidden = document.createElement('input')
        hidden.type = 'hidden'
        hidden.name = 'selected_control_ids[]'
        hidden.value = id
        hidden.id = `hidden-input-${id}`
        container.appendChild(hidden)
      })

      event.preventDefault()
      event.stopImmediatePropagation()

      this.close()
    }

    const closeBtn = this.element.querySelector("[data-action='slideover#close']") as HTMLElement
    closeBtn?.click()
  }

  restoreCheckboxStates (): void {
    this.checkboxTargets.forEach((checkbox) => {
      const savedState = this.checkboxState[checkbox.value]
      if (savedState !== undefined) {
        checkbox.checked = savedState
      }
    })
  }

  submitSelections (event: Event): void {
    event.preventDefault()

    if (!this.renderSelectedChips({ removable: true })) return

    const closeBtn = this.element.querySelector("[data-action='slideover#close']") as HTMLElement
    closeBtn?.click()
  }

  private renderSelectedChips ({ removable }: { removable: boolean }): boolean {
    const chipContainer = document.querySelector<HTMLDivElement>('#selected-controls')
    const hiddenInput = document.querySelector<HTMLInputElement>('#selected-control-ids')
    const hiddenAllControls = document.querySelector<HTMLInputElement>('#all-controls')
    const chipTemplate = document.querySelector<HTMLTemplateElement>('#chip-template')

    if ((chipContainer == null) || (hiddenInput == null) || (hiddenAllControls == null) || (chipTemplate == null)) return false

    chipContainer.innerHTML = ''

    let allControls: Array<[number, string, boolean]> = []
    try {
      allControls = JSON.parse(hiddenAllControls.value)
    } catch (e) {
      console.error('Invalid JSON in all-controls hidden field', e)
      return false
    }

    const existingIds = hiddenInput.value
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '')

    if (existingIds.length === 0) {
      this.renderEmptyState(chipContainer)
      return true
    }

    existingIds.forEach(id => {
      const matched = allControls.find(([controlId]) => String(controlId) === id)
      if (matched == null) return

      const [controlId, controlName, isActive] = matched
      const chipClone = chipTemplate.content.cloneNode(true) as HTMLElement

      const nameEl = chipClone.querySelector('.chip-name') as HTMLElement
      nameEl.textContent = controlName

      if (!isActive) {
        const iconEl = chipClone.querySelector('.chip-warning-icon') as HTMLElement

        iconEl?.classList.remove('hidden')
      }

      const closeBtn = chipClone.querySelector('.chip-remove-btn') as HTMLButtonElement
      if (removable) {
        closeBtn?.setAttribute('data-control-id', String(controlId))
      } else {
        closeBtn?.remove()
      }

      chipContainer.appendChild(chipClone)
    })

    return true
  }

  toggleCheckbox (event: Event): void {
    const checkbox = event.target as HTMLInputElement
    const container = document.querySelector<HTMLDivElement>('#selected-control-hidden-inputs')
    const hiddenInput = document.querySelector<HTMLInputElement>('#selected-control-ids')

    if (container == null || hiddenInput == null) return

    const hiddenName = 'selected_control_ids[]'
    const hiddenId = `hidden-input-${checkbox.value}`

    const existingIds = hiddenInput.value !== '' ? hiddenInput.value.split(',') : []
    existingIds.forEach(id => {
      const inputId = `hidden-input-${id}`
      if (document.getElementById(inputId) == null) {
        const hidden = document.createElement('input')
        hidden.type = 'hidden'
        hidden.name = hiddenName
        hidden.value = id
        hidden.id = inputId
        container.appendChild(hidden)
      }
    })

    const existingHiddenInput = Array.from(container.querySelectorAll<HTMLInputElement>('input[type="hidden"][name="selected_control_ids[]"]'))
      .find(input => input.value === checkbox.value)

    if (checkbox.checked && (existingHiddenInput == null)) {
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = hiddenName
      hidden.value = checkbox.value
      hidden.id = hiddenId
      container.appendChild(hidden)
    } else if (!checkbox.checked && (existingHiddenInput != null)) {
      existingHiddenInput.remove()
    }

    const currentHiddenInputs = container.querySelectorAll<HTMLInputElement>(
      'input[type="hidden"][name="selected_control_ids[]"]'
    )
    const selectedIds = Array.from(currentHiddenInputs).map(input => input.value)
    hiddenInput.value = selectedIds.join(',')
    this.notifyHiddenInputChanged(hiddenInput)
    this.syncCheckboxStates()
  }

  removeChip (event: Event): void {
    const target = event.currentTarget as HTMLElement
    const controlId = target.dataset.controlId
    if (controlId === undefined || controlId === null || controlId === '') {
      return
    }

    const chip = target.closest('.chip-control')
    chip?.remove()

    const hiddenInput = document.querySelector<HTMLInputElement>('#selected-control-ids')
    if (hiddenInput != null) {
      const ids = hiddenInput.value.split(',').map(id => id.trim()).filter(Boolean)
      hiddenInput.value = ids.filter(id => id !== controlId).join(',')
      this.notifyHiddenInputChanged(hiddenInput)
    }

    const container = document.querySelector<HTMLDivElement>('#selected-control-hidden-inputs')
    const hiddenField = container?.querySelector<HTMLInputElement>(`input[type="hidden"][value="${controlId}"]`)
    hiddenField?.remove()

    this.syncCheckboxStates()
  }

  private _handleFrameRender (event: Event): void {
    const frame = event.target as HTMLElement
    if (frame.id === 'controls-index') {
      this.syncCheckboxStates()
    }
  }

  private notifyHiddenInputChanged (input: HTMLInputElement): void {
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  private renderEmptyState (chipContainer: HTMLDivElement): void {
    if (chipContainer.dataset.emptyText === undefined || chipContainer.dataset.emptyText === '') return

    const emptyState = document.createElement('span')
    emptyState.className = 'text-sm text-secondary'
    emptyState.textContent = chipContainer.dataset.emptyText
    chipContainer.appendChild(emptyState)
  }
}
